import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';
import './PopupWMProduction.css';

const TYPE_LABELS = {
    set: 'Set', individualpacket: 'Indiv. Packet',
    wildmicr: 'Wildflowers & Microgreens', wildmicrmasterbag: 'WF/Micro Master Bag',
    fert_product: 'Fertilizer', fert_raw: 'Fert Raw',
    masterbag: 'Master Bag', smallbag: 'Small Bag',
    seeds: 'Seeds', minitools: 'Mini Tools', silicagel: 'Desiccant bag', kit: 'Kit',
};

// Types that support automatic inventory deduction (log-only otherwise)
const AUTO_DEDUCT_TYPES = new Set([
    'masterbag', 'smallbag', 'wildmicrmasterbag',
    'individualpacket', 'minitools', 'silicagel', 'readyproducts',
]);

// Finished-goods types withdrawn via real box scans below (not formula-based)
const SCAN_WITHDRAW_TYPES = new Set(['set', 'wildmicr', 'fert_product']);

// Raw-material types withdrawn via real bag scans below (not formula-based)
const BAG_WITHDRAW_TYPES = new Set(['seeds', 'fert_raw']);

// Types where the SKU/ref itself is shown instead of a resolved name
// (brand-level refs — a name lookup isn't meaningful here)
const SHOW_REF_TYPES = new Set(['minitools', 'silicagel']);

const PopupKitProduction = ({ isOpen, onClose, onSaved, mode, initialData }) => {
    const [recID,       setRecID]       = useState('');
    const [GTIN,        setGTIN]        = useState('');
    const [kitName,     setKitName]     = useState('');
    const [boxSize,     setBoxSize]     = useState(null);
    const [kitSKU,      setKitSKU]      = useState('');
    const [prodDate,    setProdDate]    = useState('');
    const [employee,    setEmployee]    = useState('');
    const [employees,   setEmployees]   = useState([]);
    const [components,  setComponents]  = useState([]);

    const [boxes,           setBoxes]           = useState([]);
    const [scanInput,       setScanInput]        = useState('');
    const [editingBoxIndex, setEditingBoxIndex]  = useState(null);
    const [editBoxValues,   setEditBoxValues]    = useState({});
    const [boxDuplicateWarn,setBoxDuplicateWarn] = useState('');

    const [fgBoxes,      setFgBoxes]      = useState([]);
    const [fgProjection, setFgProjection] = useState([]);
    const [fgScanInput,  setFgScanInput]  = useState('');
    const [fgError,      setFgError]      = useState('');

    const [sbBags,       setSbBags]       = useState([]);
    const [sbProjection, setSbProjection] = useState([]);
    const [sbScanInput,  setSbScanInput]  = useState('');
    const [sbError,      setSbError]      = useState('');
    const [sbAmbiguous,        setSbAmbiguous]        = useState(null); // { bagnumid, candidates }

    const [frBags,       setFrBags]       = useState([]);
    const [frProjection, setFrProjection] = useState([]);
    const [frScanInput,  setFrScanInput]  = useState('');
    const [frError,      setFrError]      = useState('');
    const [frAmbiguous,        setFrAmbiguous]        = useState(null); // { bagnumber, candidates }

    const [isRecordCreated, setIsRecordCreated] = useState(false);
    const [completeProduction, setCompleteProduction] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [gtinError,    setGtinError]    = useState('');
    const [saving,       setSaving]       = useState(false);

    const recIDRef = useRef('');
    const kitSKURef = useRef('');

    useEffect(() => { recIDRef.current  = recID;  }, [recID]);
    useEffect(() => { kitSKURef.current = kitSKU; }, [kitSKU]);

    function formatDateOnly(v) {
        if (!v) return '';
        if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
        return new Date(v).toISOString().split('T')[0];
    }

    function formatBoxDatetime(v) {
        if (!v) return '';
        return String(v).replace('T', ' ').replace(/\.\d+Z?$/, '').slice(0, 16);
    }

    // ── Fetch employees + initial data ────────────────────────────────────────

    useEffect(() => {
        if (!isOpen) return;

        api.get(`/api/employees-wm`)
            .then(r => setEmployees(r.data))
            .catch(() => {});

        if (mode === 'add') {
            api.get(`/api/next-kits-production-id`)
                .then(r => setRecID(r.data.nextRecId))
                .catch(() => {});

            const today = new Date().toISOString().split('T')[0];
            setGTIN(''); setKitName(''); setKitSKU(''); setComponents([]); setBoxSize(null);
            setProdDate(today); setEmployee('');
            setBoxes([]); setScanInput('');
            setFgBoxes([]); setFgProjection([]); setFgScanInput(''); setFgError('');
            setSbBags([]); setSbProjection([]); setSbScanInput(''); setSbError(''); setSbAmbiguous(null);
            setFrBags([]); setFrProjection([]); setFrScanInput(''); setFrError(''); setFrAmbiguous(null);
            setCompleteProduction(false);
            setErrorMessage(''); setGtinError(''); setBoxDuplicateWarn('');
            setEditingBoxIndex(null);
            setIsRecordCreated(false);
        } else if (mode === 'edit' && initialData) {
            setRecID(initialData.recID || '');
            setGTIN(initialData.GTIN || '');
            setKitName(initialData.kitName || '');
            setBoxSize(initialData.box_size || null);
            setKitSKU(initialData.kit_sku || '');
            setProdDate(formatDateOnly(initialData.prodDate));
            setEmployee(String(initialData.emplID || ''));
            setBoxes((initialData.boxes || []).map(b => ({
                boxNumber: b.boxnum,
                qty: b.qty,
                date_added: formatBoxDatetime(b.added_at),
            })));
            setCompleteProduction(initialData.status === 'Complete');
            setErrorMessage(''); setGtinError(''); setBoxDuplicateWarn('');
            setFgError(''); setSbError(''); setFrError('');
            setSbAmbiguous(null); setFrAmbiguous(null);
            setEditingBoxIndex(null);
            setIsRecordCreated(true);

            // Load components for the kit
            if (initialData.kit_sku) fetchComponents(initialData.kit_sku);
            if (initialData.recID) {
                fetchFinishedGoods(initialData.recID);
                fetchSeedsBags(initialData.recID);
                fetchFertRawBags(initialData.recID);
            }
        }
    }, [isOpen, mode, initialData]);

    const fetchComponents = async (sku) => {
        try {
            const r = await api.get(`/api/kits/${sku}/components`);
            setComponents(r.data || []);
        } catch { setComponents([]); }
    };

    const fetchFinishedGoods = async (id) => {
        if (!id) return;
        try {
            const r = await api.get(`/api/kits-production/${id}/finished-goods`);
            setFgBoxes((r.data.boxes || []).map(b => ({
                id: b.id,
                component_type: b.component_type,
                component_ref: b.component_ref,
                component_name: b.component_name,
                boxnum: b.boxnum,
                qty: b.qty,
                date_added: formatBoxDatetime(b.added_at),
            })));
            setFgProjection(r.data.components || []);
        } catch { /* ignore — projection is best-effort */ }
    };

    const fetchSeedsBags = async (id) => {
        if (!id) return;
        try {
            const r = await api.get(`/api/kits-production/${id}/seeds-bags`);
            setSbBags((r.data.bags || []).map(b => ({
                id: b.id,
                seeds_id: b.seeds_id,
                component_name: b.component_name,
                bagnumid: b.bagnumid,
                weight: b.weight,
                date_added: formatBoxDatetime(b.added_at),
            })));
            setSbProjection(r.data.components || []);
        } catch { /* ignore — projection is best-effort */ }
    };

    const fetchFertRawBags = async (id) => {
        if (!id) return;
        try {
            const r = await api.get(`/api/kits-production/${id}/fertraw-bags`);
            setFrBags((r.data.bags || []).map(b => ({
                id: b.id,
                raw_material_id: b.raw_material_id,
                component_name: b.component_name,
                bagnumber: b.bagnumber,
                weight: b.weight,
                date_added: formatBoxDatetime(b.added_at),
            })));
            setFrProjection(r.data.components || []);
        } catch { /* ignore — projection is best-effort */ }
    };

    // Required quantities for all withdrawal projections depend on totalBoxQty,
    // so any change to the kit's output boxes must refresh all three.
    const refreshProjections = (id) => {
        fetchFinishedGoods(id);
        fetchSeedsBags(id);
        fetchFertRawBags(id);
    };

    const fetchKitByGTIN = async (gtinVal) => {
        if (!gtinVal) return;
        setGtinError(''); setKitName(''); setComponents([]);
        try {
            const r = await api.get(`/api/kit-by-gtin/${encodeURIComponent(gtinVal)}`);
            setKitName(r.data.name || r.data.SKU);
            setBoxSize(r.data.box_size || null);
            setKitSKU(r.data.SKU);
            fetchComponents(r.data.SKU);
        } catch {
            setGtinError('Kit not found for this GTIN');
            setKitName(''); setKitSKU(''); setBoxSize(null);
        }
    };

    // ── Ensure header record exists before adding boxes ───────────────────────

    const ensureRecordExists = async () => {
        if (isRecordCreated) return true;
        if (!kitSKURef.current)  { setErrorMessage('Please enter a valid GTIN first'); return false; }
        if (!prodDate)           { setErrorMessage('Production date is required');      return false; }
        if (!employee)           { setErrorMessage('Employee is required');             return false; }
        try {
            const r = await api.post(`/api/kits-production-header`, {
                recID: null,   // always null — record doesn't exist yet
                kitSKU: kitSKURef.current,
                emplID: employee,
                prodDate,
            });
            if (!recIDRef.current) setRecID(r.data.recID);
            setIsRecordCreated(true);
            return true;
        } catch (err) {
            setErrorMessage(err.response?.data?.error || 'Failed to create production record');
            return false;
        }
    };

    // ── Boxes ─────────────────────────────────────────────────────────────────

    const handleAddBoxes = async () => {
        if (!scanInput.trim()) return;
        setErrorMessage(''); setBoxDuplicateWarn('');

        let parsed;
        try {
            const lines = scanInput.split('\n').filter(l => l.trim());
            parsed = lines.map((line, i) => {
                const parts = line.split(',').map(p => p.trim());
                if (parts.length < 3) throw new Error(`Row ${i + 1}: Invalid format`);
                // Product names may contain commas — SKU and BOX#/QTY are always the last two parts
                const scannedSKU = parts[parts.length - 2];
                if (kitSKURef.current && scannedSKU !== kitSKURef.current) {
                    throw new Error(`Row ${i + 1}: Wrong SKU — scanned "${scannedSKU}", expected "${kitSKURef.current}"`);
                }
                const [rawBox, rawQty] = parts[parts.length - 1].split('/');
                if (!rawBox || !rawQty) throw new Error(`Row ${i + 1}: Invalid BOX/QTY format`);
                return {
                    boxNumber: rawBox.replace('BOX#', '').trim(),
                    qty: parseInt(rawQty.trim(), 10),
                };
            });
        } catch (err) {
            setErrorMessage(err.message);
            return;
        }

        const ready = await ensureRecordExists();
        if (!ready) return;

        const currentRecID = recIDRef.current;
        const addedBoxes = [];

        for (const box of parsed) {
            try {
                const { data } = await api.post(`/api/kits-production-box`, {
                    recID: currentRecID,
                    boxnum: box.boxNumber,
                    qty: box.qty,
                });
                addedBoxes.push({ ...box, date_added: formatBoxDatetime(data.added_at) });
            } catch (err) {
                if (err.response?.status === 409) {
                    setBoxDuplicateWarn(err.response.data.error);
                } else {
                    setErrorMessage(err.response?.data?.error || `Failed to add box ${box.boxNumber}`);
                }
                break;
            }
        }

        if (addedBoxes.length > 0) {
            setBoxes(prev => [...prev, ...addedBoxes]);
            setScanInput('');
            refreshProjections(currentRecID);
        }
    };

    const handleRemoveBox = async (index) => {
        const box = boxes[index];
        if (isRecordCreated) {
            try {
                await api.delete(
                    `/api/kits-production-box/${encodeURIComponent(recIDRef.current)}/${encodeURIComponent(box.boxNumber)}`
                );
                refreshProjections(recIDRef.current);
            } catch (err) {
                setErrorMessage(err.response?.data?.error || `Failed to remove box ${box.boxNumber}`);
                return;
            }
        }
        if (editingBoxIndex === index) setEditingBoxIndex(null);
        setBoxes(prev => prev.filter((_, i) => i !== index));
    };

    const handleEditBox    = (i) => { setEditingBoxIndex(i); setEditBoxValues({ newBoxNumber: boxes[i].boxNumber, qty: boxes[i].qty }); };
    const handleCancelEdit = ()  => { setEditingBoxIndex(null); setEditBoxValues({}); };

    const handleSaveBoxEdit = async (index) => {
        const box = boxes[index];
        const newBox = editBoxValues.newBoxNumber?.trim() || box.boxNumber;
        const newQty = parseInt(editBoxValues.qty, 10) || box.qty;
        if (isRecordCreated) {
            try {
                await api.put(`/api/kits-production-box`, {
                    recID: recIDRef.current,
                    oldBoxnum: box.boxNumber,
                    newBoxnum: newBox,
                    qty: newQty,
                });
            } catch (err) {
                setErrorMessage(err.response?.data?.error || 'Failed to update box');
                return;
            }
            refreshProjections(recIDRef.current);
        }
        setBoxes(prev => prev.map((b, i) => i === index ? { ...b, boxNumber: newBox, qty: newQty } : b));
        setEditingBoxIndex(null); setEditBoxValues({});
    };

    // ── Finished-goods withdrawal (set / wildmicr / fert_product) ─────────────

    const handleAddFgBoxes = async () => {
        if (!fgScanInput.trim()) return;
        setFgError('');

        const fgComponentRefs = new Set(
            components
                .filter(c => SCAN_WITHDRAW_TYPES.has(c.component_type))
                .map(c => c.component_ref)
        );

        let parsed;
        try {
            const lines = fgScanInput.split('\n').filter(l => l.trim());
            parsed = lines.map((line, i) => {
                const parts = line.split(',').map(p => p.trim());
                if (parts.length < 3) throw new Error(`Row ${i + 1}: Invalid format`);
                // Product names may contain commas — SKU and BOX#/QTY are always the last two parts
                const scannedSKU = parts[parts.length - 2];
                if (!fgComponentRefs.has(scannedSKU)) {
                    throw new Error(`Row ${i + 1}: SKU "${scannedSKU}" is not a finished-goods component of this kit`);
                }
                const [rawBox] = parts[parts.length - 1].split('/');
                if (!rawBox) throw new Error(`Row ${i + 1}: Invalid BOX format`);
                return {
                    component_ref: scannedSKU,
                    boxnum: rawBox.replace('BOX#', '').trim(),
                };
            });
        } catch (err) {
            setFgError(err.message);
            return;
        }

        const ready = await ensureRecordExists();
        if (!ready) return;

        const currentRecID = recIDRef.current;
        const addedBoxes = [];

        for (const box of parsed) {
            try {
                const { data } = await api.post(`/api/kits-production-component-box`, {
                    recID: currentRecID,
                    component_ref: box.component_ref,
                    boxnum: box.boxnum,
                });
                addedBoxes.push({
                    id: data.id,
                    component_type: data.component_type,
                    component_ref: box.component_ref,
                    component_name: data.component_name,
                    boxnum: box.boxnum,
                    qty: data.qty,
                    date_added: formatBoxDatetime(new Date().toISOString()),
                });
            } catch (err) {
                setFgError(err.response?.data?.error || `Failed to withdraw box ${box.boxnum}`);
                break;
            }
        }

        if (addedBoxes.length > 0) {
            setFgBoxes(prev => [...prev, ...addedBoxes]);
            setFgScanInput('');
            fetchFinishedGoods(currentRecID);
        }
    };

    const handleRemoveFgBox = async (index) => {
        const box = fgBoxes[index];
        try {
            await api.delete(
                `/api/kits-production-component-box/${encodeURIComponent(recIDRef.current)}/${encodeURIComponent(box.boxnum)}`
            );
        } catch (err) {
            setFgError(err.response?.data?.error || `Failed to remove box ${box.boxnum}`);
            return;
        }
        setFgBoxes(prev => prev.filter((_, i) => i !== index));
        fetchFinishedGoods(recIDRef.current);
    };

    // ── Seeds bag withdrawal ────────────────────────────────────────────────

    const handleAddSeedsBag = async (bagnumidOverride, seedsIdOverride) => {
        const bagnumid = bagnumidOverride || sbScanInput.trim();
        if (!bagnumid) return;
        setSbError('');

        const ready = await ensureRecordExists();
        if (!ready) return;

        const currentRecID = recIDRef.current;
        try {
            const { data } = await api.post(`/api/kits-production-seeds-bag`, {
                recID: currentRecID,
                bagnumid,
                seeds_id: seedsIdOverride || undefined,
            });
            setSbBags(prev => [...prev, {
                id: data.id,
                seeds_id: data.seeds_id,
                component_name: data.component_name,
                bagnumid,
                weight: data.weight,
                date_added: formatBoxDatetime(new Date().toISOString()),
            }]);
            setSbScanInput('');
            setSbAmbiguous(null);
            fetchSeedsBags(currentRecID);
        } catch (err) {
            if (err.response?.status === 409 && err.response.data?.ambiguous) {
                setSbAmbiguous({ bagnumid, candidates: err.response.data.candidates || [] });
                return;
            }
            setSbError(err.response?.data?.error || `Failed to withdraw bag ${bagnumid}`);
        }
    };

    const handleResolveSeedsBag = (seedsId) => {
        if (!sbAmbiguous) return;
        handleAddSeedsBag(sbAmbiguous.bagnumid, seedsId);
    };

    const handleRemoveSeedsBag = async (index) => {
        const bag = sbBags[index];
        try {
            await api.delete(
                `/api/kits-production-seeds-bag/${encodeURIComponent(recIDRef.current)}/${encodeURIComponent(bag.bagnumid)}`
            );
        } catch (err) {
            setSbError(err.response?.data?.error || `Failed to remove bag ${bag.bagnumid}`);
            return;
        }
        setSbBags(prev => prev.filter((_, i) => i !== index));
        fetchSeedsBags(recIDRef.current);
    };

    // ── Fertilizer raw-material bag withdrawal ─────────────────────────────

    const handleAddFertRawBag = async (bagnumberOverride, rawMaterialIdOverride) => {
        const bagnumber = bagnumberOverride || frScanInput.trim();
        if (!bagnumber) return;
        setFrError('');

        const ready = await ensureRecordExists();
        if (!ready) return;

        const currentRecID = recIDRef.current;
        try {
            const { data } = await api.post(`/api/kits-production-fertraw-bag`, {
                recID: currentRecID,
                bagnumber,
                raw_material_id: rawMaterialIdOverride || undefined,
            });
            setFrBags(prev => [...prev, {
                id: data.id,
                raw_material_id: data.raw_material_id,
                component_name: data.component_name,
                bagnumber,
                weight: data.weight,
                date_added: formatBoxDatetime(new Date().toISOString()),
            }]);
            setFrScanInput('');
            setFrAmbiguous(null);
            fetchFertRawBags(currentRecID);
        } catch (err) {
            if (err.response?.status === 409 && err.response.data?.ambiguous) {
                setFrAmbiguous({ bagnumber, candidates: err.response.data.candidates || [] });
                return;
            }
            setFrError(err.response?.data?.error || `Failed to withdraw bag ${bagnumber}`);
        }
    };

    const handleResolveFertRawBag = (rawMaterialId) => {
        if (!frAmbiguous) return;
        handleAddFertRawBag(frAmbiguous.bagnumber, rawMaterialId);
    };

    const handleRemoveFertRawBag = async (index) => {
        const bag = frBags[index];
        try {
            await api.delete(
                `/api/kits-production-fertraw-bag/${encodeURIComponent(recIDRef.current)}/${encodeURIComponent(bag.bagnumber)}`
            );
        } catch (err) {
            setFrError(err.response?.data?.error || `Failed to remove bag ${bag.bagnumber}`);
            return;
        }
        setFrBags(prev => prev.filter((_, i) => i !== index));
        fetchFertRawBags(recIDRef.current);
    };

    // ── Save ─────────────────────────────────────────────────────────────────

    const handleSave = async () => {
        if (!kitSKU)   { setErrorMessage('Kit GTIN / SKU is required');    return; }
        if (!prodDate) { setErrorMessage('Production date is required');    return; }
        if (!employee) { setErrorMessage('Employee is required');           return; }
        setErrorMessage(''); setSaving(true);

        try {
            // Ensure header is saved / updated.
            // Only pass recID if the DB row already exists; the pre-fetched "next ID"
            // is display-only and must not be sent as if it were an existing record.
            const r = await api.post(`/api/kits-production-header`, {
                recID: isRecordCreated ? (recIDRef.current || null) : null,
                kitSKU,
                emplID: employee,
                prodDate,
            });
            if (!isRecordCreated) setRecID(r.data.recID);
            setIsRecordCreated(true);

            // If marking complete, trigger write-off
            if (completeProduction) {
                await api.put(`/api/kits-production-complete/${r.data.recID}`);
            }

            onSaved?.();
            onClose();
        } catch (err) {
            setErrorMessage(err.response?.data?.error || 'Failed to save production record');
        } finally {
            setSaving(false);
        }
    };

    // ── Materials write-off preview ───────────────────────────────────────────

    const totalBoxQty = boxes.reduce((s, b) => s + (parseInt(b.qty) || 0), 0);

    const materialsPreview = components.map(c => ({
        type_label:    TYPE_LABELS[c.component_type] || c.component_type,
        component_ref: c.component_ref,
        display_name:  SHOW_REF_TYPES.has(c.component_type) ? c.component_ref : (c.component_name || c.component_ref),
        qty_per_kit:   parseFloat(c.quantity),
        total:         parseFloat(c.quantity) * totalBoxQty,
        unit:          c.unit || 'pcs',
        auto_deduct:   AUTO_DEDUCT_TYPES.has(c.component_type),
        scan_withdraw: SCAN_WITHDRAW_TYPES.has(c.component_type) || BAG_WITHDRAW_TYPES.has(c.component_type),
    }));

    const hasBoxWithdrawComponents = components.some(c => SCAN_WITHDRAW_TYPES.has(c.component_type));
    const hasSeedsComponents       = components.some(c => c.component_type === 'seeds');
    const hasFertRawComponents     = components.some(c => c.component_type === 'fert_raw');

    if (!isOpen) return null;

    return (
        <div className="popup open">
            <div className="popup__body popup__body--wide">
                <div className="popup__content popup__content--wide">
                    <button className="popup__close-button" onClick={onClose}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <use xlinkHref="#closeBtn" />
                        </svg>
                    </button>

                    <h2 className="popup__title">Kit Production</h2>

                    {/* ── Header row ── */}
                    <div className="prod-header-row">
                        <div className="prod-header-field">
                            <label className="label">Record ID</label>
                            <input type="text" className="form__input" value={recID} readOnly />
                        </div>

                        <div className="prod-header-field">
                            <label className="label">GTIN</label>
                            <input
                                type="text"
                                className="form__input"
                                value={GTIN}
                                onChange={e => { setGTIN(e.target.value); setKitName(''); setGtinError(''); setComponents([]); }}
                                onBlur={() => fetchKitByGTIN(GTIN)}
                                onKeyDown={e => { if (e.key === 'Enter') fetchKitByGTIN(GTIN); }}
                                placeholder="Scan or enter kit GTIN"
                                readOnly={mode === 'edit'}
                            />
                            {gtinError && <span className="field-error">{gtinError}</span>}
                            {kitName && (
                                <div className="gtin-product-info">
                                    <span className="gtin-product-name">{kitName}</span>
                                    <span className="gtin-packs-per-box">{components.length} component{components.length !== 1 ? 's' : ''}</span>
                                    {boxSize && <span className="gtin-box-size" title="Box size, inches"><i className="fa-solid fa-box"></i>{boxSize}</span>}
                                </div>
                            )}
                        </div>

                        <div className="prod-header-field">
                            <label className="label">Production Date</label>
                            <input type="date" className="form__input" value={prodDate} onChange={e => setProdDate(e.target.value)} />
                        </div>

                        <div className="prod-header-field">
                            <label className="label">Employee</label>
                            <select className="slct" value={employee} onChange={e => setEmployee(e.target.value)}>
                                <option value="">Select Employee</option>
                                {employees.map(emp => (
                                    <option key={emp.employeeID} value={emp.employeeID}>{emp.fullName || emp.employeeName}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* ── Two-column section ── */}
                    <div className="prod-columns-row">

                        {/* LEFT: Boxes scanned */}
                        <div className="prod-column">
                            <div className="prod-column-header">
                                <span className="prod-column-title">Boxes Produced</span>
                            </div>
                            <div className="prod-scan-area">
                                <textarea
                                    className="scannerform__textarea"
                                    rows="4"
                                    placeholder="Scan boxes here (one per line)"
                                    value={scanInput}
                                    onChange={e => setScanInput(e.target.value)}
                                />
                                <button className="btn btn__white_g prod-add-btn" onClick={handleAddBoxes}>ADD</button>
                            </div>
                            {boxDuplicateWarn && <div className="field-error">{boxDuplicateWarn}</div>}
                            {boxes.length > 0 && (
                                <table className="prod-table">
                                    <thead>
                                        <tr>
                                            <th>Box #</th>
                                            <th>QTY</th>
                                            <th>Added</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {boxes.map((box, i) => (
                                            <tr key={i}>
                                                {editingBoxIndex === i ? (
                                                    <>
                                                        <td><input className="prod-inline-input" value={editBoxValues.newBoxNumber} onChange={e => setEditBoxValues(v => ({ ...v, newBoxNumber: e.target.value }))} /></td>
                                                        <td><input className="prod-inline-input prod-inline-input--sm" type="number" value={editBoxValues.qty} onChange={e => setEditBoxValues(v => ({ ...v, qty: e.target.value }))} /></td>
                                                        <td>{box.date_added}</td>
                                                        <td className="col-actions">
                                                            <div className="prod-table__actions">
                                                                <button className="prod-table__icon-btn prod-table__icon-btn--confirm" title="Save" onClick={() => handleSaveBoxEdit(i)}><i className="fas fa-check" /></button>
                                                                <button className="prod-table__icon-btn" title="Cancel" onClick={handleCancelEdit}><i className="fas fa-times" /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td>{box.boxNumber}</td>
                                                        <td>{box.qty}</td>
                                                        <td>{box.date_added}</td>
                                                        <td className="col-actions">
                                                            <div className="prod-table__actions">
                                                                <button className="prod-table__icon-btn" title="Edit" onClick={() => handleEditBox(i)}><i className="fas fa-pen" /></button>
                                                                <button className="prod-table__icon-btn prod-table__icon-btn--danger" title="Remove" onClick={() => handleRemoveBox(i)}><i className="fas fa-trash" /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="2"><strong>{boxes.length} boxes · {totalBoxQty} kits</strong></td>
                                            <td colSpan="2" />
                                        </tr>
                                    </tfoot>
                                </table>
                            )}
                        </div>

                        {/* RIGHT: Materials write-off (live — happens per box scan) */}
                        <div className="prod-column">
                            <div className="prod-column-header">
                                <span className="prod-column-title">Materials Write-off</span>
                                {totalBoxQty > 0 && (
                                    <span className="prod-column-subtitle">written off for {totalBoxQty} kit{totalBoxQty !== 1 ? 's' : ''}</span>
                                )}
                            </div>

                            {materialsPreview.length === 0 ? (
                                <div style={{ color: '#94a3b8', fontSize: 13, padding: '12px 0' }}>
                                    {kitSKU ? 'No components defined for this kit' : 'Enter GTIN to see components'}
                                </div>
                            ) : (
                                <table className="prod-table">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Component</th>
                                            <th>Per kit</th>
                                            <th>Total</th>
                                            <th title="Auto-deducted from inventory">Auto</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {materialsPreview.map((m, i) => (
                                            <tr key={i}>
                                                <td style={{ color: '#64748b', fontSize: 11 }}>{m.type_label}</td>
                                                <td>{m.display_name}</td>
                                                <td>{m.qty_per_kit} {m.unit}</td>
                                                <td style={totalBoxQty > 0 ? { fontWeight: 600 } : { color: '#94a3b8' }}>
                                                    {totalBoxQty > 0 ? `${m.total} ${m.unit}` : '—'}
                                                </td>
                                                <td title={
                                                    m.auto_deduct ? 'Inventory will be deducted automatically'
                                                    : m.scan_withdraw ? 'Scan below to withdraw it'
                                                    : 'Log only — manual reconciliation needed'
                                                }>
                                                    {m.auto_deduct
                                                        ? <i className="fa fa-check" style={{ color: '#22c55e' }} />
                                                        : m.scan_withdraw
                                                        ? <i className="fa fa-barcode" style={{ color: '#15803d' }} />
                                                        : <i className="far fa-clock" style={{ color: '#f59e0b' }} />}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {materialsPreview.some(m => !m.auto_deduct && !m.scan_withdraw) && (
                                <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 8 }}>
                                    <i className="far fa-clock" /> = logged only, reconcile manually
                                </div>
                            )}
                            {materialsPreview.some(m => m.scan_withdraw) && (
                                <div style={{ fontSize: 11, color: '#15803d', marginTop: 4 }}>
                                    <i className="fa fa-barcode" /> = scan below to withdraw it from warehouse stock
                                </div>
                            )}

                            {/* Finished-goods withdrawal — set / wildmicr / fert_product, scanned as real boxes */}
                            {hasBoxWithdrawComponents && (
                                <div className="prod-fg-section" style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                                    <div className="prod-column-header">
                                        <span className="prod-column-title">Finished Goods Withdrawal</span>
                                    </div>
                                    <div className="prod-scan-area">
                                        <textarea
                                            className="scannerform__textarea"
                                            rows="3"
                                            placeholder="Scan finished-goods box here"
                                            value={fgScanInput}
                                            onChange={e => setFgScanInput(e.target.value)}
                                        />
                                        <button className="btn btn__white_g prod-add-btn" onClick={handleAddFgBoxes}>ADD</button>
                                    </div>
                                    {fgError && <div className="field-error">{fgError}</div>}

                                    {fgBoxes.length > 0 && (
                                        <table className="prod-table">
                                            <thead>
                                                <tr>
                                                    <th>Type</th>
                                                    <th>Component</th>
                                                    <th>Box #</th>
                                                    <th>Qty</th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {fgBoxes.map((b, i) => (
                                                    <tr key={i}>
                                                        <td style={{ color: '#64748b', fontSize: 11 }}>{TYPE_LABELS[b.component_type] || b.component_type}</td>
                                                        <td>{b.component_name || b.component_ref}</td>
                                                        <td>{b.boxnum}</td>
                                                        <td>{b.qty}</td>
                                                        <td className="col-actions">
                                                            <button className="prod-table__icon-btn prod-table__icon-btn--danger" title="Remove" onClick={() => handleRemoveFgBox(i)}><i className="fas fa-trash" /></button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    {fgProjection.length > 0 && (
                                        <table className="prod-table" style={{ marginTop: 8 }}>
                                            <thead>
                                                <tr>
                                                    <th>Component</th>
                                                    <th title="quantity_per_kit × kits produced so far">Required</th>
                                                    <th>Withdrawn</th>
                                                    <th>Stock now</th>
                                                    <th title="Withdrawn minus required — surplus already pulled but unused (negative = still need more)">Will remain</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {fgProjection.map((p, i) => (
                                                    <tr key={i}>
                                                        <td>{p.component_name || p.component_ref}</td>
                                                        <td>{p.required}</td>
                                                        <td>{p.withdrawn}</td>
                                                        <td>{p.stockNow}</td>
                                                        <td style={p.projectedRemaining < 0 ? { color: '#ef4444', fontWeight: 600 } : { fontWeight: 600 }}>
                                                            {p.projectedRemaining}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}

                            {/* Seeds withdrawal — scanned by bagnumid, whole bag consumed */}
                            {hasSeedsComponents && (
                                <div className="prod-fg-section" style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                                    <div className="prod-column-header">
                                        <span className="prod-column-title">Seeds Withdrawal</span>
                                    </div>
                                    <div className="prod-scan-area">
                                        <input
                                            type="text"
                                            className="form__input"
                                            placeholder="Scan seed bag number, press Enter"
                                            value={sbScanInput}
                                            onChange={e => setSbScanInput(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleAddSeedsBag(); }}
                                        />
                                        <button className="btn btn__white_g prod-add-btn" onClick={handleAddSeedsBag}>ADD</button>
                                    </div>
                                    {sbError && <div className="field-error">{sbError}</div>}

                                    {sbBags.length > 0 && (
                                        <table className="prod-table">
                                            <thead>
                                                <tr>
                                                    <th>Seed</th>
                                                    <th>Bag #</th>
                                                    <th>Weight</th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sbBags.map((b, i) => (
                                                    <tr key={i}>
                                                        <td>{b.component_name || b.seeds_id}</td>
                                                        <td>{b.bagnumid}</td>
                                                        <td>{b.weight} lb</td>
                                                        <td className="col-actions">
                                                            <button className="prod-table__icon-btn prod-table__icon-btn--danger" title="Remove" onClick={() => handleRemoveSeedsBag(i)}><i className="fas fa-trash" /></button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    {sbProjection.length > 0 && (
                                        <table className="prod-table" style={{ marginTop: 8 }}>
                                            <thead>
                                                <tr>
                                                    <th>Component</th>
                                                    <th title="quantity_per_kit × kits produced so far">Required</th>
                                                    <th>Withdrawn</th>
                                                    <th>Stock now</th>
                                                    <th title="Withdrawn minus required — surplus already pulled but unused (negative = still need more)">Will remain</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sbProjection.map((p, i) => (
                                                    <tr key={i}>
                                                        <td>{p.component_name || p.component_ref}</td>
                                                        <td>{p.required} lb</td>
                                                        <td>{p.withdrawn} lb</td>
                                                        <td>{p.stockNow} lb</td>
                                                        <td style={p.projectedRemaining < 0 ? { color: '#ef4444', fontWeight: 600 } : { fontWeight: 600 }}>
                                                            {p.projectedRemaining} lb
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}

                            {/* Fertilizer raw materials withdrawal — scanned by bag number, whole bag consumed */}
                            {hasFertRawComponents && (
                                <div className="prod-fg-section" style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                                    <div className="prod-column-header">
                                        <span className="prod-column-title">Fert Raw Materials Withdrawal</span>
                                    </div>
                                    <div className="prod-scan-area">
                                        <input
                                            type="text"
                                            className="form__input"
                                            placeholder="Scan raw material bag number, press Enter"
                                            value={frScanInput}
                                            onChange={e => setFrScanInput(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleAddFertRawBag(); }}
                                        />
                                        <button className="btn btn__white_g prod-add-btn" onClick={handleAddFertRawBag}>ADD</button>
                                    </div>
                                    {frError && <div className="field-error">{frError}</div>}

                                    {frBags.length > 0 && (
                                        <table className="prod-table">
                                            <thead>
                                                <tr>
                                                    <th>Material</th>
                                                    <th>Bag #</th>
                                                    <th>Weight</th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {frBags.map((b, i) => (
                                                    <tr key={i}>
                                                        <td>{b.component_name || b.raw_material_id}</td>
                                                        <td>{b.bagnumber}</td>
                                                        <td>{b.weight} lb</td>
                                                        <td className="col-actions">
                                                            <button className="prod-table__icon-btn prod-table__icon-btn--danger" title="Remove" onClick={() => handleRemoveFertRawBag(i)}><i className="fas fa-trash" /></button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    {frProjection.length > 0 && (
                                        <table className="prod-table" style={{ marginTop: 8 }}>
                                            <thead>
                                                <tr>
                                                    <th>Component</th>
                                                    <th title="quantity_per_kit × kits produced so far">Required</th>
                                                    <th>Withdrawn</th>
                                                    <th>Stock now</th>
                                                    <th title="Withdrawn minus required — surplus already pulled but unused (negative = still need more)">Will remain</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {frProjection.map((p, i) => (
                                                    <tr key={i}>
                                                        <td>{p.component_name || p.component_ref}</td>
                                                        <td>{p.required} lb</td>
                                                        <td>{p.withdrawn} lb</td>
                                                        <td>{p.stockNow} lb</td>
                                                        <td style={p.projectedRemaining < 0 ? { color: '#ef4444', fontWeight: 600 } : { fontWeight: 600 }}>
                                                            {p.projectedRemaining} lb
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </div>

                    </div>

                    {/* ── Footer ── */}
                    <div className="prod-footer-row">
                        <label className="prod-complete-label">
                            <input
                                type="checkbox"
                                checked={completeProduction}
                                disabled={mode === 'edit' && initialData?.status === 'Complete'}
                                onChange={e => setCompleteProduction(e.target.checked)}
                            />
                            <span>Mark as Complete</span>
                        </label>
                        {errorMessage && <div className="popup__error-message">{errorMessage}</div>}
                        <button className="btn btn__green" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>

                </div>

                {/* ── Ambiguous bag pickers ── shown when a scanned bag number matches
                    more than one component in this kit's composition */}
                {(sbAmbiguous || frAmbiguous) && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(15, 23, 42, 0.45)', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <div style={{
                            background: '#fff', borderRadius: 8, padding: 20,
                            width: 420, maxWidth: '90%', boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                        }}>
                            <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Which one is this bag?</h3>
                            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748b' }}>
                                Bag "{sbAmbiguous ? sbAmbiguous.bagnumid : frAmbiguous.bagnumber}" matches more than one component in this kit. Pick the correct one.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {(sbAmbiguous ? sbAmbiguous.candidates : frAmbiguous.candidates).map((c, i) => (
                                    <button
                                        key={i}
                                        className="btn btn__white_g"
                                        style={{ textAlign: 'left' }}
                                        onClick={() => sbAmbiguous
                                            ? handleResolveSeedsBag(c.seeds_id)
                                            : handleResolveFertRawBag(c.raw_material_id)}
                                    >
                                        {c.component_name || c.seeds_id || c.raw_material_id} — {c.weight} lb
                                    </button>
                                ))}
                            </div>
                            <button
                                className="btn btn__white_g"
                                style={{ marginTop: 14 }}
                                onClick={() => { setSbAmbiguous(null); setFrAmbiguous(null); }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PopupKitProduction;
